/**
 * MemberOutreachTrackingTab — billing-agnostic outreach tracking on the member
 * record. Shown in the "Outreach" work-tab for orgs WITHOUT the `outreach_billing`
 * capability (billing-enabled orgs get MemberOutreachHistoryTab instead).
 *
 * Staff can log SMS / email / phone touches and record an in-person visit captured
 * via an intake/assessment form, and see the unified touch trail. No claims/$ — this
 * is engagement tracking. Data: GET/POST /api/member-outreach/{member_id}/... .
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Phone, MessageSquare, Mail, Users, Loader2, Plus, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Enrollment } from '@/services/api/members';
import { FormInstanceEditor } from '@/components/forms/FormInstanceEditorByOrg';
import {
  memberOutreachApi,
  MemberOutreachTouches,
  MemberOutreachTouch,
  InPersonFormOption,
  OutreachChannel,
  OutreachTemplate,
} from '@/services/api/memberOutreach';
import { formatLocalDateTime } from '@/utils/dateTime';

const channelIcon = (c: OutreachChannel) =>
  c === 'phone' ? (
    <Phone className="w-3.5 h-3.5" />
  ) : c === 'email' ? (
    <Mail className="w-3.5 h-3.5" />
  ) : c === 'in_person' ? (
    <Users className="w-3.5 h-3.5" />
  ) : (
    <MessageSquare className="w-3.5 h-3.5" />
  );

const channelLabel: Record<OutreachChannel, string> = {
  sms: 'SMS',
  email: 'Email',
  phone: 'Phone',
  in_person: 'In-person',
};

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try {
    return formatLocalDateTime(d, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d;
  }
};

interface Props {
  memberId: number;
  memberName?: string;
  enrollments?: Enrollment[];
}

export default function MemberOutreachTrackingTab({ memberId, memberName, enrollments = [] }: Props) {
  const [data, setData] = useState<MemberOutreachTouches | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Composer state — real sends + templates + engagement types, matching billing orgs.
  const [channel, setChannel] = useState<'sms' | 'email' | 'phone'>('sms');
  const [minutes, setMinutes] = useState<string>('');
  const [engagementType, setEngagementType] = useState<string>('');
  const [engagementOptions, setEngagementOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [templates, setTemplates] = useState<OutreachTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [toContact, setToContact] = useState<string>('');
  const [placeCall, setPlaceCall] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  // In-person modal state.
  const [inPersonOpen, setInPersonOpen] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    memberOutreachApi
      .getTouches(memberId)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e: any) => {
        if (alive) setError(e?.response?.data?.detail || e?.message || 'Failed to load outreach touches');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [memberId]);

  useEffect(() => load(), [load]);

  // Engagement-type options (configured per program, or defaults) — same list billing orgs see.
  useEffect(() => {
    let alive = true;
    memberOutreachApi
      .getTypeConfig(memberId)
      .then((cfg) => {
        if (alive) setEngagementOptions(cfg.engagement_types || []);
      })
      .catch(() => {
        /* non-fatal: fall back to free-text engagement type */
      });
    return () => {
      alive = false;
    };
  }, [memberId]);

  // Templates for the selected channel (sms/email); phone has none.
  useEffect(() => {
    let alive = true;
    setTemplateId('');
    if (channel === 'phone') {
      setTemplates([]);
      return;
    }
    memberOutreachApi
      .getTemplates(channel)
      .then((tpls) => {
        if (alive) setTemplates(tpls);
      })
      .catch(() => {
        if (alive) setTemplates([]);
      });
    return () => {
      alive = false;
    };
  }, [channel]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const tpl = templates.find((t) => String(t.id) === id);
    if (tpl) {
      if (tpl.body) setMessage(tpl.body);
      if (channel === 'email' && tpl.subject) setSubject(tpl.subject);
    }
  };

  const resetComposer = () => {
    setMinutes('');
    setEngagementType('');
    setTemplateId('');
    setSubject('');
    setMessage('');
    setToContact('');
    setPlaceCall(false);
  };

  const submitTouch = async () => {
    setSaving(true);
    setError(null);
    setSendResult(null);
    try {
      const eng = engagementType.trim() || undefined;
      const mins = minutes ? Number(minutes) : undefined;
      const tid = templateId ? Number(templateId) : undefined;
      if (channel === 'sms') {
        const r = await memberOutreachApi.sendSms(memberId, {
          message: message.trim() || undefined,
          template_id: tid,
          minutes: mins,
          to_number: toContact.trim() || undefined,
          engagement_type: eng,
        });
        setSendResult(r.sent ? 'SMS sent.' : `SMS logged (not sent${r.error ? `: ${r.error}` : ''}).`);
      } else if (channel === 'email') {
        const r = await memberOutreachApi.sendEmail(memberId, {
          subject: subject.trim() || undefined,
          message: message.trim() || undefined,
          template_id: tid,
          minutes: mins,
          to_email: toContact.trim() || undefined,
          engagement_type: eng,
        });
        setSendResult(r.sent ? 'Email sent.' : `Email logged (not sent${r.error ? `: ${r.error}` : ''}).`);
      } else {
        const r = await memberOutreachApi.logCall(memberId, {
          minutes: mins,
          notes: message.trim() || undefined,
          to_number: toContact.trim() || undefined,
          initiate: placeCall,
          engagement_type: eng,
        });
        setSendResult(r.dialed ? 'Call placed and logged.' : 'Call logged.');
      }
      resetComposer();
      load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to record touch');
    } finally {
      setSaving(false);
    }
  };

  const counts = data?.counts;

  const summaryTiles = useMemo(
    () => [
      { label: 'Total touches', value: counts?.total ?? 0 },
      { label: 'SMS', value: counts?.sms ?? 0 },
      { label: 'Email', value: counts?.email ?? 0 },
      { label: 'Phone', value: counts?.phone ?? 0 },
      { label: 'In-person', value: counts?.in_person ?? 0 },
    ],
    [counts],
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading outreach touches…
      </div>
    );
  }

  const touches = data?.touches ?? [];

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">{error}</div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {summaryTiles.map((s) => (
          <div key={s.label} className="rounded-lg border bg-white dark:bg-gray-800 px-3 py-2">
            <div className="text-xs text-gray-500">{s.label}</div>
            <div className="text-lg font-semibold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Composer — reach out (real SMS/email/call) or record a touch */}
      <section className="rounded-lg border p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Outreach touch
        </h3>

        {/* Channel picker */}
        <div className="flex flex-wrap gap-2 mb-3">
          {(['sms', 'email', 'phone'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setChannel(c)}
              className={`px-3 py-1.5 text-sm rounded-md inline-flex items-center gap-1.5 transition-colors ${
                channel === c
                  ? 'bg-v2accent text-white'
                  : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
              }`}
            >
              {channelIcon(c)} {channelLabel[c]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {channel !== 'phone' && (
            <label className="text-xs text-gray-500 flex flex-col gap-1">
              Template
              <select
                className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                value={templateId}
                onChange={(e) => applyTemplate(e.target.value)}
              >
                <option value="">— none —</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            Engagement type
            {engagementOptions.length > 0 ? (
              <select
                className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                value={engagementType}
                onChange={(e) => setEngagementType(e.target.value)}
              >
                <option value="">— select —</option>
                {engagementOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                value={engagementType}
                onChange={(e) => setEngagementType(e.target.value)}
                placeholder="e.g. follow-up"
              />
            )}
          </label>
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            Minutes
            <input
              type="number"
              min={0}
              className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="e.g. 10"
            />
          </label>
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            {channel === 'email' ? 'To (email, optional)' : 'To (phone, optional)'}
            <input
              className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={toContact}
              onChange={(e) => setToContact(e.target.value)}
              placeholder={channel === 'email' ? "leave blank to use member's email" : "leave blank to use member's phone"}
            />
          </label>
        </div>

        {channel === 'email' && (
          <label className="text-xs text-gray-500 flex flex-col gap-1 mt-3">
            Subject
            <input
              className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
            />
          </label>
        )}

        <label className="text-xs text-gray-500 flex flex-col gap-1 mt-3">
          {channel === 'phone' ? 'Call notes' : 'Message'}
          <textarea
            className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
            rows={channel === 'phone' ? 2 : 3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={channel === 'phone' ? 'What was discussed?' : 'Message to send…'}
          />
        </label>

        {channel === 'phone' && (
          <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
            <input type="checkbox" checked={placeCall} onChange={(e) => setPlaceCall(e.target.checked)} />
            Place the call now (auto-dial)
          </label>
        )}

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button size="sm" onClick={submitTouch} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : channel === 'sms' ? (
              'Send SMS'
            ) : channel === 'email' ? (
              'Send email'
            ) : (
              'Log call'
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setInPersonOpen(true)}>
            <Users className="w-4 h-4 mr-1.5" /> Log in-person visit
          </Button>
          {sendResult && <span className="text-xs text-green-600">{sendResult}</span>}
          <span className="text-xs text-gray-400">In-person visits are captured by completing an intake/assessment form.</span>
        </div>
      </section>

      {/* Touch trail */}
      <section>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4" /> Outreach touches ({touches.length})
        </h3>
        {touches.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            No outreach touches recorded yet.
            <div className="text-xs text-gray-400 mt-1">Log an SMS, email, phone, or in-person touch above.</div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Channel</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Direction</th>
                  <th className="text-right px-3 py-2">Minutes</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {touches.map((tc: MemberOutreachTouch) => (
                  <tr key={tc.id} className="border-t">
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        {channelIcon(tc.channel)} {channelLabel[tc.channel]}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(tc.date)}</td>
                    <td className="px-3 py-2 capitalize">{tc.direction === 'in_person' ? '—' : tc.direction}</td>
                    <td className="px-3 py-2 text-right">{tc.minutes ?? '—'}</td>
                    <td className="px-3 py-2">
                      {tc.engagement_type ? <Badge variant="secondary">{tc.engagement_type}</Badge> : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-md">
                      {tc.channel === 'in_person' && tc.form_name ? (
                        <div className="truncate" title={tc.form_name}>
                          <span className="font-medium">{tc.form_name}</span>
                          {tc.location ? <span className="text-gray-400"> · {tc.location}</span> : null}
                          {tc.notes ? <span className="text-gray-400"> · {tc.notes}</span> : null}
                        </div>
                      ) : (
                        <span className="truncate block" title={tc.notes}>
                          {tc.notes || '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {inPersonOpen && (
        <InPersonVisitModal
          memberId={memberId}
          memberName={memberName}
          enrollments={enrollments}
          onClose={() => setInPersonOpen(false)}
          onRecorded={() => {
            setInPersonOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * In-person visit modal: pick an enrollment + intake/assessment form + visit
 * details, fill the form (FormInstanceEditor creates + submits the instance),
 * then record the completion as an in-person outreach touch.
 */
function InPersonVisitModal({
  memberId,
  memberName,
  enrollments,
  onClose,
  onRecorded,
}: {
  memberId: number;
  memberName?: string;
  enrollments: Enrollment[];
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [forms, setForms] = useState<InPersonFormOption[]>([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [enrollmentId, setEnrollmentId] = useState<number | null>(enrollments[0]?.id ?? null);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [minutes, setMinutes] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [filling, setFilling] = useState(false);
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadingForms(true);
    memberOutreachApi
      .getInPersonForms(memberId)
      .then((f) => {
        if (!alive) return;
        setForms(f);
        setTemplateId(f[0]?.id ?? null);
      })
      .catch((e: any) => {
        if (alive) setError(e?.response?.data?.detail || e?.message || 'Failed to load forms');
      })
      .finally(() => {
        if (alive) setLoadingForms(false);
      });
    return () => {
      alive = false;
    };
  }, [memberId]);

  const canFill = !!templateId && !!enrollmentId;

  const onFormSubmitted = async (instanceId: number) => {
    setRecording(true);
    setError(null);
    try {
      await memberOutreachApi.recordInPersonTouch(memberId, {
        form_instance_id: instanceId,
        minutes: minutes ? Number(minutes) : undefined,
        location: location.trim() || undefined,
        notes: notes.trim() || undefined,
        engagement_type: 'in_person_visit',
      });
      onRecorded();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Form submitted, but recording the touch failed');
      setFilling(false);
      setRecording(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">
            Log in-person visit{memberName ? ` · ${memberName}` : ''}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">{error}</div>
          )}

          {!filling ? (
            <>
              {enrollments.length === 0 ? (
                <div className="p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md">
                  This member has no program enrollment. Enroll them in a program first to record an in-person visit.
                </div>
              ) : (
                <>
                  {enrollments.length > 1 && (
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Program enrollment
                      <select
                        className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                        value={enrollmentId ?? ''}
                        onChange={(e) => setEnrollmentId(Number(e.target.value))}
                      >
                        {enrollments.map((en) => (
                          <option key={en.id} value={en.id}>
                            {en.program_name || `Program #${en.program_id}`} ({en.status})
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  <label className="text-xs text-gray-500 flex flex-col gap-1">
                    Form
                    {loadingForms ? (
                      <span className="text-sm text-gray-400 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading forms…
                      </span>
                    ) : forms.length === 0 ? (
                      <span className="text-sm text-amber-700">
                        No intake/assessment forms are configured for this member's program(s).
                      </span>
                    ) : (
                      <select
                        className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                        value={templateId ?? ''}
                        onChange={(e) => setTemplateId(Number(e.target.value))}
                      >
                        {forms.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name} ({f.form_type})
                          </option>
                        ))}
                      </select>
                    )}
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Minutes
                      <input
                        type="number"
                        min={0}
                        className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                        value={minutes}
                        onChange={(e) => setMinutes(e.target.value)}
                        placeholder="e.g. 30"
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Location
                      <input
                        className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. member's home"
                      />
                    </label>
                  </div>
                  <label className="text-xs text-gray-500 flex flex-col gap-1">
                    Notes
                    <textarea
                      className="border rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </label>

                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button size="sm" disabled={!canFill} onClick={() => setFilling(true)}>
                      Open form
                    </Button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div>
              {recording && (
                <div className="mb-3 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Recording visit…
                </div>
              )}
              <FormInstanceEditor
                templateId={templateId!}
                enrollmentId={enrollmentId!}
                onClose={onClose}
                onSubmit={(instance) => {
                  if (instance?.id) onFormSubmitted(instance.id);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
